/**
 * Access Templates — the heart of Access Control V3.
 *
 * A template is a named bundle: for every module, one of four levels
 * (None / View / Standard / Admin) plus optional granular extras.
 * Assign templates to people (company default, or per project) in the
 * Assignments tab.
 *
 * The inner view takes an injected `api` so it can be rendered and tested
 * without MSAL; the default export wires it to the live session.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  LEVELS, LevelSegmented, GranularChip, makeApi,
  sectionCard, inputStyle, primaryBtn, subtleBtn, errorBanner,
} from './AccessControlShared';

export const AccessTemplatesView = ({ api, initialData = null }) => {
  const [meta, setMeta] = useState(initialData?.meta || null);
  const [templates, setTemplates] = useState(initialData?.templates || []);
  const [selectedId, setSelectedId] = useState(initialData?.selectedId ?? initialData?.templates?.[0]?.id ?? null);
  const [edits, setEdits] = useState({}); // moduleId -> { level, granular }
  const [loading, setLoading] = useState(!initialData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [copyFromCurrent, setCopyFromCurrent] = useState(true);

  const load = useCallback(async (keepSelection = true) => {
    try {
      setLoading(true);
      setError(null);
      const [metaRes, templatesRes] = await Promise.all([
        api('GET', '/access-control/meta'),
        api('GET', '/access-control/templates'),
      ]);
      setMeta(metaRes.data);
      setTemplates(templatesRes.data);
      setSelectedId(prev => (keepSelection && templatesRes.data.some(t => t.id === prev))
        ? prev
        : templatesRes.data[0]?.id ?? null);
      setEdits({});
    } catch (e) {
      console.error(e);
      setError('Failed to load templates. ' + (e.message || ''));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!initialData) load();
  }, [initialData, load]);

  const selected = templates.find(t => t.id === selectedId) || null;
  const modules = meta?.modules || [];
  const granularByModule = useMemo(() => {
    const map = {};
    for (const g of (meta?.granularPermissions || [])) {
      (map[g.module_key] = map[g.module_key] || []).push(g);
    }
    return map;
  }, [meta]);

  const gridFor = useCallback((template) => {
    const byModule = {};
    for (const row of (template?.modules || [])) {
      byModule[row.module_id] = { level: row.level, granular: row.granular || [] };
    }
    return byModule;
  }, []);

  const baseGrid = useMemo(() => gridFor(selected), [selected, gridFor]);
  const cell = (moduleId) => edits[moduleId] || baseGrid[moduleId] || { level: 0, granular: [] };
  const isDirty = Object.keys(edits).length > 0;
  const readOnly = !!selected?.is_system;

  const setLevel = (moduleId, level) => {
    setEdits(prev => ({ ...prev, [moduleId]: { ...cell(moduleId), level } }));
  };

  const toggleGranular = (moduleId, key) => {
    const current = cell(moduleId);
    const has = current.granular.includes(key);
    setEdits(prev => ({
      ...prev,
      [moduleId]: { ...current, granular: has ? current.granular.filter(g => g !== key) : [...current.granular, key] },
    }));
  };

  const save = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      setError(null);
      const payload = modules.map(m => {
        const c = cell(m.id);
        return { moduleId: m.id, level: c.level, granular: c.granular };
      });
      await api('PUT', `/access-control/templates/${selected.id}/modules`, { modules: payload });
      await load();
    } catch (e) {
      console.error(e);
      setError('Failed to save. ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const createTemplate = async () => {
    if (!newName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const res = await api('POST', '/access-control/templates', {
        name: newName.trim(),
        copyFromTemplateId: copyFromCurrent && selected ? selected.id : undefined,
      });
      setShowNewForm(false);
      setNewName('');
      await load(false);
      setSelectedId(res.data.id);
    } catch (e) {
      console.error(e);
      setError('Failed to create template. ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async () => {
    if (!selected || selected.is_system) return;
    if (!window.confirm(`Delete template "${selected.name}"? Only possible when nobody is assigned to it.`)) return;
    try {
      setError(null);
      await api('DELETE', `/access-control/templates/${selected.id}`);
      await load(false);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to delete template.');
    }
  };

  const filteredTemplates = templates.filter(t =>
    !search.trim() || t.name.toLowerCase().includes(search.toLowerCase()) || t.key.includes(search.toLowerCase()));

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>Loading templates…</div>;
  }

  return (
    <div>
      {errorBanner(error)}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Template list */}
        <div style={{ ...sectionCard, width: 290, flexShrink: 0 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', display: 'flex', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Search templates…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button style={primaryBtn()} onClick={() => setShowNewForm(v => !v)} title="Create a new template">+</button>
          </div>
          {showNewForm && (
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
              <input
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
                placeholder="New template name"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#555', marginBottom: 8 }}>
                <input type="checkbox" checked={copyFromCurrent} onChange={e => setCopyFromCurrent(e.target.checked)} />
                Start as a copy of “{selected?.name || '—'}”
              </label>
              <button style={primaryBtn(!newName.trim() || saving)} disabled={!newName.trim() || saving} onClick={createTemplate}>
                Create
              </button>
            </div>
          )}
          <div style={{ maxHeight: 560, overflowY: 'auto' }}>
            {filteredTemplates.map(t => (
              <button
                key={t.id}
                onClick={() => { setSelectedId(t.id); setEdits({}); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                  border: 'none', borderBottom: '1px solid #f1f3f5', cursor: 'pointer',
                  backgroundColor: t.id === selectedId ? '#e7f1ff' : '#fff',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t.name} {t.is_system && <span title="System template — read-only">🔒</span>}
                </div>
                <div style={{ fontSize: 11, color: '#868e96' }}>
                  {t.userCount} user{t.userCount === 1 ? '' : 's'}
                  {t.projectCount > 0 && ` · ${t.projectCount} project hat${t.projectCount === 1 ? '' : 's'}`}
                </div>
              </button>
            ))}
            {filteredTemplates.length === 0 && (
              <div style={{ padding: 20, fontSize: 13, color: '#868e96' }}>No templates match.</div>
            )}
          </div>
        </div>

        {/* Module grid for the selected template */}
        <div style={{ ...sectionCard, flex: 1, minWidth: 560 }}>
          {!selected ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>Select a template.</div>
          ) : (
            <>
              <div style={{
                padding: '14px 18px', borderBottom: '1px solid #e9ecef',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
                    {selected.name} {selected.is_system && <span style={{ fontSize: 12, color: '#e74c3c' }}>(system — read-only)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#868e96' }}>
                    Assigned to {selected.userCount} user{selected.userCount === 1 ? '' : 's'} · key: <code>{selected.key}</code>
                  </div>
                </div>
                {!readOnly && (
                  <>
                    {isDirty && <span style={{ fontSize: 12, color: '#e67e22', fontWeight: 600 }}>Unsaved changes</span>}
                    <button style={subtleBtn} disabled={!isDirty || saving} onClick={() => setEdits({})}>Discard</button>
                    <button style={primaryBtn(!isDirty || saving)} disabled={!isDirty || saving} onClick={save}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                    {selected.userCount === 0 && (
                      <button style={{ ...subtleBtn, color: '#dc3545', borderColor: '#f1aeb5' }} onClick={deleteTemplate}>Delete</button>
                    )}
                  </>
                )}
              </div>

              <div style={{ padding: '6px 18px 4px', fontSize: 12, color: '#868e96' }}>
                Pick a level per module. <strong>Extras</strong> appear for View/Standard; Admin includes everything automatically.
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                    <th style={{ textAlign: 'left', padding: '8px 18px', color: '#495057' }}>Module</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#495057' }}>Access level</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#495057' }}>Extras (granular)</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m, idx) => {
                    const c = cell(m.id);
                    const registry = granularByModule[m.key] || [];
                    return (
                      <tr key={m.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc', borderBottom: '1px solid #f1f3f5' }}>
                        <td style={{ padding: '10px 18px', verticalAlign: 'top', width: 220 }}>
                          <div style={{ fontWeight: 600, color: '#212529' }}>{m.name}</div>
                          <div style={{ fontSize: 11, color: '#adb5bd' }}>{m.key}</div>
                        </td>
                        <td style={{ padding: '10px 12px', verticalAlign: 'top', width: 280 }}>
                          <LevelSegmented value={c.level} onChange={level => setLevel(m.id, level)} disabled={readOnly} />
                        </td>
                        <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                          {c.level === 3 ? (
                            <span style={{ fontSize: 12, color: '#e74c3c', fontWeight: 600 }}>All extras included</span>
                          ) : c.level === 0 ? (
                            <span style={{ fontSize: 12, color: '#ced4da' }}>—</span>
                          ) : registry.length === 0 ? (
                            <span style={{ fontSize: 12, color: '#ced4da' }}>No extras defined</span>
                          ) : (
                            registry.map(g => (
                              <GranularChip
                                key={g.key}
                                label={g.name}
                                title={g.description}
                                active={c.granular.includes(g.key)}
                                onClick={readOnly ? null : () => toggleGranular(m.id, g.key)}
                              />
                            ))
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ padding: '12px 18px', borderTop: '1px solid #e9ecef', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {LEVELS.map(l => (
                  <span key={l.value} style={{ fontSize: 12, color: '#6c757d' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, backgroundColor: l.color, marginRight: 5 }} />
                    <strong>{l.label}</strong> — {l.hint}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AccessTemplatesTab = () => {
  const { instance, accounts } = useMsal();
  const api = useMemo(() => makeApi(instance, accounts), [instance, accounts]);
  return <AccessTemplatesView api={api} />;
};

export default AccessTemplatesTab;
